import { useEffect, lazy, Suspense } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { Navbar, Footer, AgencyChatWidget } from '@/src/components';
import { ANIMATIONS } from '@/src/constants';

// Lazy load pages
const Home = lazy(() => import('@/src/pages/Home'));

// Services
const ServicesIndex = lazy(() => import('@/src/pages/services/index'));
const ConsultoriaPage = lazy(() => import('@/src/pages/services/ConsultoriaPage'));
const ChatbotCrewaiPage = lazy(() => import('@/src/pages/services/ChatbotCrewaiPage'));
const ChatbotN8nPage = lazy(() => import('@/src/pages/services/ChatbotN8nPage'));
const AutomatizacionPage = lazy(() => import('@/src/pages/services/AutomatizacionPage'));
const AgentsCrewPage = lazy(() => import('@/src/pages/services/AgentsCrewPage'));
const AirbnbBookingPage = lazy(() => import('@/src/pages/services/AirbnbBookingPage'));
const GuestlyPage = lazy(() => import('@/src/pages/services/GuestlyPage'));

// Empresa
const EmpresaIndex = lazy(() => import('@/src/pages/empresa/index'));
const SobreNosotrosPage = lazy(() => import('@/src/pages/empresa/SobreNosotrosPage'));
const BlogPage = lazy(() => import('@/src/pages/empresa/BlogPage'));
const BlogPostPage = lazy(() => import('@/src/pages/empresa/BlogPostPage'));
const ContactoPage = lazy(() => import('@/src/pages/empresa/ContactoPage'));

// Legal
const LegalIndexPage = lazy(() => import('@/src/pages/legal/LegalIndexPage'));
const PrivacidadPage = lazy(() => import('@/src/pages/legal/PrivacidadPage'));
const TerminosPage = lazy(() => import('@/src/pages/legal/TerminosPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Component to handle scrolling to hash anchors
function HashLinkHandler() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash, pathname]);

  return null;
}

function Layout() {
  const { scrollY } = useScroll();
  const smoothScrollY = useSpring(scrollY, ANIMATIONS.springConfig);

  const y1 = useTransform(smoothScrollY, [0, 2000], [0, 600]);
  const y2 = useTransform(smoothScrollY, [0, 2000], [0, -400]);
  const opacity = useTransform(smoothScrollY, [0, 1000], [1, 0.4]);

  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-hidden selection:bg-white/20 selection:text-white">
      <HashLinkHandler />

      {/* Background Elements Parallax */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <motion.div
          style={{ y: y1, opacity }}
          className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-900/10 blur-[120px] rounded-full will-change-transform"
        />
        <motion.div
          style={{ y: y2, opacity }}
          className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-900/10 blur-[120px] rounded-full will-change-transform"
        />
      </div>

      <Navbar />

      <main className="relative z-10">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Home */}
            <Route path="/" element={<Home />} />

            {/* Services */}
            <Route path="/servicios" element={<ServicesIndex />} />
            <Route path="/servicios/consultoria" element={<ConsultoriaPage />} />
            <Route path="/servicios/chatbot-crewai" element={<ChatbotCrewaiPage />} />
            <Route path="/servicios/chatbot-n8n" element={<ChatbotN8nPage />} />
            <Route path="/servicios/automatizacion" element={<AutomatizacionPage />} />
            <Route path="/servicios/agents-crew" element={<AgentsCrewPage />} />
            <Route path="/servicios/airbnb-booking" element={<AirbnbBookingPage />} />
            <Route path="/guestly" element={<GuestlyPage />} />

            {/* Empresa */}
            <Route path="/empresa" element={<EmpresaIndex />} />
            <Route path="/empresa/sobre-nosotros" element={<SobreNosotrosPage />} />
            <Route path="/empresa/blog" element={<BlogPage />} />
            <Route path="/empresa/blog/:slug" element={<BlogPostPage />} />
            <Route path="/empresa/contacto" element={<ContactoPage />} />

            {/* Legal */}
            <Route path="/legal" element={<LegalIndexPage />} />
            <Route path="/privacidad" element={<PrivacidadPage />} />
            <Route path="/terminos" element={<TerminosPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <AgencyChatWidget />
    </div>
  );
}

function App() {
  return (
    <>
      <Router>
        <Layout />
      </Router>
      <Analytics />
    </>
  );
}

export default App;
